import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaEnvelope, 
  // FaPhone, 
  FaMapMarkerAlt, 
  // FaClock, 
  FaPaperPlane,
  // FaUser,
  // FaBuilding,
  // FaGlobe
} from 'react-icons/fa';
import { ink, paper, paperMuted, borderInk, borderInkHover, bodyMuted } from '../theme';
import BlueprintSectionTitle from './BlueprintSectionTitle';

const ContactSection = styled.section`
  padding: 100px 0;
  background: ${paper};
  position: relative;
  font-family: 'Inter', system-ui, sans-serif;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  text-align: center;
  margin-bottom: 3rem;
  color: ${bodyMuted};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  @media (max-width: 480px) {
    gap: 30px;
  }

  @media (max-width: 360px) {
    gap: 25px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: 768px) {
    gap: 25px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }

  @media (max-width: 360px) {
    gap: 18px;
  }
`;

const ContactCard = styled.div`
  background: ${paperMuted};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 25px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  position: relative;
  box-shadow: 0 2px 10px rgba(3, 26, 56, 0.04);

  &:hover {
    border-color: ${borderInkHover};
    box-shadow: 0 6px 18px rgba(3, 26, 56, 0.06);
  }

  @media (max-width: 768px) {
    padding: 20px 15px;
  }

  @media (max-width: 480px) {
    padding: 18px 12px;
  }

  @media (max-width: 360px) {
    padding: 15px 10px;
  }
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(3, 26, 56, 0.07);
  border: 1px solid ${borderInk};
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: ${ink};
  margin-bottom: 15px;

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 1.2rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    width: 35px;
    height: 35px;
    font-size: 1rem;
    margin-bottom: 8px;
  }
`;

const ContactTitle = styled.h3`
  color: ${ink};
  font-size: 1.05rem;
  margin-bottom: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 1.05rem;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 6px;
  }

  @media (max-width: 360px) {
    font-size: 0.95rem;
    margin-bottom: 5px;
  }
`;

const ContactText = styled.p`
  color: ${bodyMuted};
  line-height: 1.6;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 0.85rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    line-height: 1.4;
  }

  @media (max-width: 360px) {
    font-size: 0.75rem;
    line-height: 1.3;
  }
`;

const ContactForm = styled.form`
  background: ${paperMuted};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 35px;
  position: relative;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);

  @media (max-width: 768px) {
    padding: 30px 25px;
  }

  @media (max-width: 480px) {
    padding: 25px 20px;
  }

  @media (max-width: 360px) {
    padding: 20px 15px;
  }
`;

const FormTitle = styled.h3`
  color: ${ink};
  font-size: 1rem;
  margin-bottom: 28px;
  font-weight: 800;
  text-align: center;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 1.15rem;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    font-size: 1.05rem;
    margin-bottom: 20px;
  }

  @media (max-width: 360px) {
    font-size: 1rem;
    margin-bottom: 18px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    margin-bottom: 18px;
  }

  @media (max-width: 360px) {
    margin-bottom: 15px;
  }
`;

const FormLabel = styled.label`
  display: block;
  color: ${ink};
  margin-bottom: 8px;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 6px;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-bottom: 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.7rem;
    margin-bottom: 4px;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 18px;
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  color: ${ink};
  font-size: 0.9rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: #9aa0a6;
  }

  &:focus {
    outline: none;
    border-color: ${ink};
    box-shadow: 0 0 0 3px rgba(3, 26, 56, 0.1);
  }

  @media (max-width: 768px) {
    padding: 10px 15px;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 0.8rem;
  }

  @media (max-width: 360px) {
    padding: 8px 10px;
    font-size: 0.75rem;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 18px;
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  color: ${ink};
  font-size: 0.9rem;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: #9aa0a6;
  }

  &:focus {
    outline: none;
    border-color: ${ink};
    box-shadow: 0 0 0 3px rgba(3, 26, 56, 0.1);
  }

  @media (max-width: 768px) {
    padding: 10px 15px;
    font-size: 0.85rem;
    min-height: 100px;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 0.8rem;
    min-height: 90px;
  }

  @media (max-width: 360px) {
    padding: 8px 10px;
    font-size: 0.75rem;
    min-height: 80px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px 25px;
  background: ${ink};
  color: #ffffff;
  border: 1px solid ${ink};
  border-radius: 2px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.2);

  &:hover {
    transform: translateY(-2px);
    background: #052a52;
    box-shadow: 0 8px 22px rgba(3, 26, 56, 0.28);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.88rem;
  }

  @media (max-width: 480px) {
    padding: 10px 18px;
    font-size: 0.85rem;
  }

  @media (max-width: 360px) {
    padding: 8px 15px;
    font-size: 0.8rem;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(76, 175, 80, 0.12);
  border: 1px solid rgba(76, 175, 80, 0.35);
  color: #2e7d32;
  padding: 18px;
  border-radius: 12px;
  text-align: center;
  margin-top: 20px;
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.35);
  color: #c62828;
  padding: 18px;
  border-radius: 12px;
  text-align: center;
  margin-top: 20px;
`;

const CaptchaWrapper = styled.div`
  margin: 20px 0;
  padding: 16px;
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
`;

const CaptchaQuestion = styled.div`
  color: ${ink};
  font-size: 0.9rem;
  margin-bottom: 10px;
  font-weight: 600;
`;

const CaptchaInput = styled.input`
  width: 100%;
  padding: 10px 15px;
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  color: ${ink};
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: #9aa0a6;
  }

  &:focus {
    outline: none;
    border-color: ${ink};
    box-shadow: 0 0 0 3px rgba(3, 26, 56, 0.1);
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 0.9rem;
  }
`;

const CaptchaRefresh = styled.button`
  margin-top: 10px;
  padding: 8px 15px;
  background: rgba(3, 26, 56, 0.06);
  border: 1px solid ${borderInk};
  color: ${ink};
  font-size: 0.8rem;
  border-radius: 2px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(3, 26, 56, 0.1);
    border-color: ${borderInkHover};
  }
`;

// Генерация математической задачи
const generateMathCaptcha = () => {
  const num1 = Math.floor(Math.random() * 20) + 1; // 1-20
  const num2 = Math.floor(Math.random() * 20) + 1; // 1-20
  const operators = ['+', '-', '*'];
  const operator = operators[Math.floor(Math.random() * operators.length)];
  
  let answer;
  let question;
  
  switch (operator) {
    case '+':
      answer = num1 + num2;
      question = `${num1} + ${num2}`;
      break;
    case '-':
      // Убеждаемся, что результат положительный
      const maxNum = Math.max(num1, num2);
      const minNum = Math.min(num1, num2);
      answer = maxNum - minNum;
      question = `${maxNum} - ${minNum}`;
      break;
    case '*':
      // Для умножения используем меньшие числа
      const small1 = Math.floor(Math.random() * 10) + 1;
      const small2 = Math.floor(Math.random() * 10) + 1;
      answer = small1 * small2;
      question = `${small1} × ${small2}`;
      break;
    default:
      answer = num1 + num2;
      question = `${num1} + ${num2}`;
  }
  
  return { question, answer };
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    captchaAnswer: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [captcha, setCaptcha] = useState(() => generateMathCaptcha());
  
  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  // Генерация новой капчи
  const refreshCaptcha = () => {
    setCaptcha(generateMathCaptcha());
    setFormData(prev => ({ ...prev, captchaAnswer: '' }));
    if (error) setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Очищаем ошибку при изменении полей
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Проверка капчи на клиенте (дополнительная проверка)
    const userAnswer = parseInt(formData.captchaAnswer);
    if (isNaN(userAnswer) || userAnswer !== captcha.answer) {
      setError('Неверный ответ на математическую задачу. Пожалуйста, попробуйте еще раз.');
      refreshCaptcha();
      return;
    }

    setIsSubmitting(true);

    try {
      // Отправляем данные на сервер
      const response = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          captchaAnswer: userAnswer,
          captchaId: captcha.question // Отправляем вопрос для проверки на сервере
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Ошибка при отправке сообщения');
      }

      // Успешная отправка
      setIsSubmitted(true);
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        message: '',
        captchaAnswer: ''
      });

      // Генерируем новую капчу
      refreshCaptcha();

      // Скрыть сообщение об успехе через 5 секунд
      setTimeout(() => setIsSubmitted(false), 5000);

    } catch (err) {
      console.error('Ошибка отправки формы:', err);
      setError(err.message || 'Произошла ошибка при отправке сообщения. Пожалуйста, попробуйте позже.');
      refreshCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      title: "Email",
      text: "info@entcor.ru\nsupport@entcor.ru"
    },
    // {
    //   icon: <FaPhone />,
    //   title: "Телефон",
    //   text: "+7 (924) 386-23-34"
    // },
    {
      icon: <FaMapMarkerAlt />,
      title: "Адрес",
      text: "672039, Россия, г. Чита, ул. Проезжая, 46\nОфис 212"
    },
  ];

  return (
    <ContactSection id="contact">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <BlueprintSectionTitle>Свяжитесь с нами</BlueprintSectionTitle>
          <SectionSubtitle>
            Свяжитесь с нашей командой для получения 
            дополнительной информации и демонстрации возможностей системы.
          </SectionSubtitle>
        </motion.div>

        <ContactGrid>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <ContactInfo>
              {contactInfo.map((info, index) => (
                <ContactCard key={index}>
                  <ContactIcon>
                    {info.icon}
                  </ContactIcon>
                  <ContactTitle>{info.title}</ContactTitle>
                  <ContactText style={{ whiteSpace: 'pre-line' }}>
                    {info.text}
                  </ContactText>
                </ContactCard>
              ))}
            </ContactInfo>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <ContactForm onSubmit={handleSubmit}>
              <FormTitle>Отправить сообщение</FormTitle>
              
              <FormGroup>
                <FormLabel>Имя *</FormLabel>
                <FormInput
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Введите ваше имя"
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Компания</FormLabel>
                <FormInput
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Название компании"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Email *</FormLabel>
                <FormInput
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Телефон</FormLabel>
                <FormInput
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+7 (999) 123-45-67"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Сообщение *</FormLabel>
                <FormTextarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Опишите ваш запрос или вопрос..."
                  required
                />
              </FormGroup>

              <FormGroup>
                <CaptchaWrapper>
                  <CaptchaQuestion>
                    Задача для защиты от спама: {captcha.question} = ?
                  </CaptchaQuestion>
                  <CaptchaInput
                    type="number"
                    name="captchaAnswer"
                    value={formData.captchaAnswer}
                    onChange={handleInputChange}
                    placeholder="Введите ответ"
                    required
                    autoComplete="off"
                  />
                  <CaptchaRefresh type="button" onClick={refreshCaptcha}>
                    Обновить задачу
                  </CaptchaRefresh>
                </CaptchaWrapper>
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <div className="spinner"></div>
                    Отправка...
                  </>
                ) : (
                  <>
                    <FaPaperPlane />
                    Отправить сообщение
                  </>
                )}
              </SubmitButton>

              {error && (
                <ErrorMessage
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  {error}
                </ErrorMessage>
              )}

              {isSubmitted && (
                <SuccessMessage
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  Спасибо! Ваше сообщение успешно отправлено. Мы свяжемся с вами в ближайшее время.
                </SuccessMessage>
              )}
            </ContactForm>
          </motion.div>
        </ContactGrid>
      </Container>
    </ContactSection>
  );
};

export default Contact;
